import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Shield, Target, Users, Zap, CheckCircle, Ship, Globe as GlobeIcon, FileText, Clock } from 'lucide-react';
const services = [{
  icon: Users,
  title: 'Crew Recruitment',
  description: 'Full-cycle recruitment for all ranks and positions'
}, {
  icon: Shield,
  title: 'Screening & Verification',
  description: 'Document verification, background checks, and compliance'
}, {
  icon: FileText,
  title: 'Compliance Management',
  description: 'STCW, MLC, and flag state requirements'
}, {
  icon: Target,
  title: 'Coordination',
  description: 'Crew changes, travel arrangements, and logistics'
}];
const coverage = [{
  category: 'Vessel Types',
  items: ['Tankers (Oil, Chemical, LNG)', 'Bulk Carriers', 'Container Vessels', 'Offshore Vessels', 'Cruise Ships', 'Specialized Vessels']
}, {
  category: 'Regions',
  items: ['Europe', 'Middle East', 'Asia Pacific', 'Americas', 'Africa']
}, {
  category: 'Positions',
  items: ['Deck Officers', 'Engine Officers', 'Ratings (AB, OS, Oiler)', 'Electro-Technical Officers', 'Catering Staff']
}];
const process = [{
  step: '01',
  title: 'Request',
  description: 'Submit your crew requirements and vessel details'
}, {
  step: '02',
  title: 'Matching',
  description: 'We match qualified seafarers from our database'
}, {
  step: '03',
  title: 'Screening',
  description: 'Document verification and compliance checks'
}, {
  step: '04',
  title: 'Interview',
  description: 'Arrange interviews and assessments'
}, {
  step: '05',
  title: 'Deployment',
  description: 'Contract signing and crew mobilization'
}];
const faqs = [{
  question: 'What is your recruitment process timeline?',
  answer: 'Typically 2-4 weeks from request to crew deployment, depending on position availability and requirements.'
}, {
  question: 'Do you handle crew changes and logistics?',
  answer: 'Yes, we coordinate crew changes, travel arrangements, visas, and all logistics related to crew mobilization.'
}, {
  question: 'What compliance standards do you follow?',
  answer: 'We ensure full compliance with STCW, MLC 2006, flag state requirements, and company-specific standards.'
}, {
  question: 'Do you provide replacement crew if needed?',
  answer: 'Yes, we provide replacement crew in case of emergencies or if a seafarer is unable to complete the contract.'
}];
export function CompaniesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return <div className="min-h-screen bg-white" data-id="element-261">
      <Header data-id="element-262" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-16 bg-gradient-to-br from-[var(--color-dark)] to-[var(--color-dark-lighter)]" data-id="element-263">
        <div className="max-w-4xl mx-auto text-center" data-id="element-264">
          <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-265">
            <span className="text-sm font-medium text-white" data-id="element-266">
              For Companies
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-267">
            Reliable Crew Supply for Vessels & Offshore Projects
          </h1>
          <p className="text-xl text-gray-300 mb-8" data-id="element-268">
            Professional recruitment, screening, and crew management services
            for shipowners and operators
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-269">
            <a href="#request-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[var(--color-tan)] text-white text-base font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-270">
              Request Crew
            </a>
            <a href="#contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[var(--color-dark)] text-base font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-271">
              Discuss Your Task
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-272">
        <div className="max-w-7xl mx-auto" data-id="element-273">
          <div className="text-center mb-12" data-id="element-274">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-275">
              Services for Companies
            </h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-2xl mx-auto" data-id="element-276">
              Comprehensive crew management solutions for maritime operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-277">
            {services.map((service, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-278">
                <service.icon className="w-8 h-8 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-279" />
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-280">
                  {service.title}
                </h3>
                <p className="text-sm text-[var(--color-text-light)]" data-id="element-281">
                  {service.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-282">
        <div className="max-w-7xl mx-auto" data-id="element-283">
          <div className="text-center mb-12" data-id="element-284">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-285">
              Coverage
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-286">
              Vessels, regions, and positions we recruit for
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-287">
            {coverage.map((item, index) => <div key={index} className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-288">
                <h3 className="text-xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-289">
                  {item.category}
                </h3>
                <ul className="space-y-2" data-id="element-290">
                  {item.items.map((subitem, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-[var(--color-text-dark)]" data-id="element-291">
                      <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-292" />
                      <span data-id="element-293">{subitem}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-294">
        <div className="max-w-7xl mx-auto" data-id="element-295">
          <div className="text-center mb-12" data-id="element-296">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-297">
              How We Work
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-298">
              Transparent process from request to crew deployment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8" data-id="element-299">
            {process.map((item, index) => <div key={index} className="relative" data-id="element-300">
                <div className="flex flex-col items-center text-center" data-id="element-301">
                  <div className="w-16 h-16 rounded-full bg-[var(--color-tan)] text-white flex items-center justify-center text-xl font-bold mb-4" data-id="element-302">
                    {item.step}
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-303">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[var(--color-text-light)]" data-id="element-304">
                    {item.description}
                  </p>
                </div>
                {index < process.length - 1 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-200" data-id="element-305" />}
              </div>)}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-306">
        <div className="max-w-4xl mx-auto text-center" data-id="element-307">
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-308">
            Compliance & Confidentiality
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-309">
            We ensure full compliance with international maritime standards and
            maintain strict confidentiality
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-310">
            <div className="p-6 bg-white border border-gray-200 rounded-xl" data-id="element-311">
              <Shield className="w-8 h-8 text-[var(--color-tan)] mx-auto mb-4" data-id="element-312" />
              <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-313">
                NDA Protection
              </h3>
              <p className="text-sm text-[var(--color-text-light)]" data-id="element-314">
                All information protected by non-disclosure agreements
              </p>
            </div>
            <div className="p-6 bg-white border border-gray-200 rounded-xl" data-id="element-315">
              <FileText className="w-8 h-8 text-[var(--color-tan)] mx-auto mb-4" data-id="element-316" />
              <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-317">
                Document Verification
              </h3>
              <p className="text-sm text-[var(--color-text-light)]" data-id="element-318">
                Thorough verification of all certificates and documents
              </p>
            </div>
            <div className="p-6 bg-white border border-gray-200 rounded-xl" data-id="element-319">
              <CheckCircle className="w-8 h-8 text-[var(--color-tan)] mx-auto mb-4" data-id="element-320" />
              <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-321">
                STCW & MLC Compliance
              </h3>
              <p className="text-sm text-[var(--color-text-light)]" data-id="element-322">
                Full compliance with international maritime regulations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section id="request-form" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-323">
        <div className="max-w-4xl mx-auto" data-id="element-324">
          <div className="text-center mb-8" data-id="element-325">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-326">
              Request Crew
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-327">
              Tell us about your requirements and we'll provide qualified
              candidates
            </p>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-328">
            <form className="space-y-5" data-id="element-329">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-330">
                <div data-id="element-331">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-332">
                    Company Name <span className="text-red-500" data-id="element-333">*</span>
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-334" />
                </div>
                <div data-id="element-335">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-336">
                    Contact Person <span className="text-red-500" data-id="element-337">*</span>
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-338" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-339">
                <div data-id="element-340">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-341">
                    Email <span className="text-red-500" data-id="element-342">*</span>
                  </label>
                  <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-343" />
                </div>
                <div data-id="element-344">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-345">
                    Phone <span className="text-red-500" data-id="element-346">*</span>
                  </label>
                  <input type="tel" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-347" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-348">
                <div data-id="element-349">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-350">
                    Vessel / Project Type{' '}
                    <span className="text-red-500" data-id="element-351">*</span>
                  </label>
                  <input type="text" placeholder="e.g., Chemical Tanker" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-352" />
                </div>
                <div data-id="element-353">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-354">
                    Region / Area <span className="text-red-500" data-id="element-355">*</span>
                  </label>
                  <input type="text" placeholder="e.g., Middle East" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-356" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-357">
                <div data-id="element-358">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-359">
                    Positions Needed (Rank){' '}
                    <span className="text-red-500" data-id="element-360">*</span>
                  </label>
                  <input type="text" placeholder="e.g., Chief Engineer, 2nd Officer" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-361" />
                </div>
                <div data-id="element-362">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-363">
                    Quantity <span className="text-red-500" data-id="element-364">*</span>
                  </label>
                  <input type="number" placeholder="Number of crew needed" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-365" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-366">
                <div data-id="element-367">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-368">
                    Contract Duration <span className="text-red-500" data-id="element-369">*</span>
                  </label>
                  <input type="text" placeholder="e.g., 6+1 months" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-370" />
                </div>
                <div data-id="element-371">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-372">
                    Start Date <span className="text-red-500" data-id="element-373">*</span>
                  </label>
                  <input type="date" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-374" />
                </div>
              </div>

              <div data-id="element-375">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-376">
                  Additional Requirements / Notes
                </label>
                <textarea rows={4} placeholder="Job description, specific requirements, certifications needed..." className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none" data-id="element-377" />
              </div>

              <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400 transition-colors" data-id="element-378">
                <div className="flex items-center justify-center gap-2 text-[var(--color-text-light)] text-sm" data-id="element-379">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-380">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" data-id="element-381" />
                  </svg>
                  <span data-id="element-382">Attach files (Job description, requirements)</span>
                </div>
              </div>

              <div className="flex justify-center pt-2" data-id="element-383">
                <button type="submit" className="px-8 py-4 bg-[var(--color-tan)] text-white text-base font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-384">
                  Submit Request
                </button>
              </div>

              <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-385">
                All information is treated confidentially under NDA. We respond
                within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-386">
        <div className="max-w-4xl mx-auto" data-id="element-387">
          <div className="text-center mb-12" data-id="element-388">
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-389">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200" data-id="element-390">
            <div className="space-y-0" data-id="element-391">
              {faqs.map((faq, index) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-392">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" data-id="element-393">
                    <span className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-394">
                      {faq.question}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-395">
                      <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-396">
                        {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-397" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-398" />}
                      </svg>
                    </div>
                  </button>
                  {openFaq === index && <div className="pb-5" data-id="element-399">
                      <p className="text-sm text-[var(--color-text-light)]" data-id="element-400">
                        {faq.answer}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-401">
        <div className="max-w-4xl mx-auto text-center" data-id="element-402">
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-403">
            Get in Touch
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-404">
            Contact us for crew requests, consultations, or partnership
            opportunities
          </p>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-405">
            <a href="https://t.me/elvora" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-406">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0088cc" data-id="element-407">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-408" />
              </svg>
              <span className="text-sm font-medium" data-id="element-409">Telegram</span>
            </a>
            <a href="https://wa.me/380" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-410">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366" data-id="element-411">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" data-id="element-412" />
              </svg>
              <span className="text-sm font-medium" data-id="element-413">WhatsApp</span>
            </a>
            <a href="mailto:info@elvora.com" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-414">
              <svg className="w-5 h-5 text-[var(--color-tan)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-415">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-416" />
              </svg>
              <span className="text-sm font-medium" data-id="element-417">Email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-418" />
    </div>;
}